import { Bell, CheckCheck, Ellipsis } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { NotificationBellContent } from "./NotificationBellContent";
import { NotificationStatusTabs } from "./NotificationStatusTabs";
import { useNotificationActionMutation } from "../api/useNotificationActionMutation";
import { useNotificationsPage } from "../api/useNotificationsPage";
import { BELL_FILTERS, NOTIFICATION_ACTIONS } from "../constants";
import { useNotificationContext } from "../context";
import { notificationStatusSchema } from "../schemas";
import type { NotificationStatus } from "../types";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/v2/popover";
import { ROUTES } from "@/constants/routes";

const BELL_STATUSES: readonly NotificationStatus[] = [
  notificationStatusSchema.enum.unread,
  notificationStatusSchema.enum.inbox,
];

export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<NotificationStatus>(BELL_FILTERS.status);
  const { unreadCount } = useNotificationContext();
  const unreadPage = useNotificationsPage(BELL_FILTERS);
  const notificationAction = useNotificationActionMutation();
  const newestUnread = unreadPage.data?.pages[0]?.data[0];

  const close = useCallback(() => setOpen(false), []);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setStatus(BELL_FILTERS.status);
  };

  const handleOpenAutoFocus = (event: Event) => {
    event.preventDefault();
    if (!(event.target instanceof HTMLElement)) return;
    event.target.focus();
  };

  const handleMarkAllAsRead = () => {
    if (!newestUnread) return;
    notificationAction.mutate({
      action: NOTIFICATION_ACTIONS.READ,
      target: { filter: BELL_FILTERS, createdAtOrBefore: newestUnread.createdAt, excludedIds: [] },
    });
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8"
          aria-label={
            unreadCount > 0
              ? t("notifications.bellUnreadLabel", { count: unreadCount })
              : t("notifications.title")
          }
        >
          <Bell className="size-4" />
          {unreadCount > 0 && (
            <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(45rem,calc(100vw-2rem))] bg-card p-0"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <div className="flex items-center justify-between gap-2 px-4 pt-2">
          <h4 className="text-sm font-semibold">{t("notifications.title")}</h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={t("common.actions")}>
                <Ellipsis aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={!newestUnread || notificationAction.isPending}
                onSelect={handleMarkAllAsRead}
              >
                <CheckCheck aria-hidden="true" />
                {t("notifications.markAllAsRead")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <NotificationStatusTabs statuses={BELL_STATUSES} status={status} onChange={setStatus} />
        <NotificationBellContent filters={{ ...BELL_FILTERS, status }} onNavigate={close} />
        <div className="border-t">
          <Link
            to={`/${ROUTES.notifications}`}
            onClick={close}
            className="block px-4 py-3 text-sm font-medium text-primary hover-surface-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            {t("notifications.page.viewNotificationCenter")}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
